import React, { useCallback, useEffect, useMemo, useState } from "react";
import { axiosSalary } from "../../api/axiosInstance";
import { FaFileExcel } from "react-icons/fa";
import { useAuth } from "../../auth/AuthContext";

const PeriodicReportByRest = () => {

  const { user } = useAuth();
  const groupId = user?.groupId;
  const restId = user?.restaurantId; // ✅ Fixed from auth — no restaurant selector needed

  /* ================= STATE ================= */

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH REPORT ================= */

  const fetchReport = useCallback(async () => {

    if (!groupId || !restId || !fromDate || !toDate) return;

    try {

      setLoading(true);

      const res = await axiosSalary.get(
        `/DailyAttendanceReport/DailyReportByRestaurant?groupId=${groupId}&restId=${restId}&fromDate=${fromDate}&toDate=${toDate}`
      );

      setData(res.data || []);

    } catch (err) {

      console.error("Attendance Report Error:", err);
      setData([]);

    } finally {

      setLoading(false);
    }

  }, [groupId, restId, fromDate, toDate]);

  /* ================= USE EFFECT ================= */

  useEffect(() => {

    if (fromDate && toDate) {
      fetchReport();
    }

  }, [fetchReport, fromDate, toDate]);

  /* ================= TOTAL DAYS ================= */

  const totalDaysRange = useMemo(() => {

    if (!fromDate || !toDate) return 0;

    const start = new Date(fromDate);
    const end = new Date(toDate);

    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

  }, [fromDate, toDate]);

  /* ================= GROUP BY EMPLOYEE ================= */

  const groupedEmployees = useMemo(() => {

    return Object.values(

      data.reduce((acc, row) => {

        if (!acc[row.empId]) {
          acc[row.empId] = [];
        }

        acc[row.empId].push(row);

        return acc;

      }, {})

    );

  }, [data]);

  /* ================= CSV ESCAPE HELPER ================= */

  const escapeCSV = (value) => {

    if (value === null || value === undefined) return "";

    const str = String(value);

    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }

    return str;
  };

  /* ================= EXPORT CSV ================= */

  const exportExcel = () => {

    if (!groupedEmployees.length) return;

    let rows = [];

    groupedEmployees.forEach((empRows) => {

      const emp = empRows[0];

      const summaryRow = {
        EmployeeName: emp.empName,
        EmployeeId: emp.empId,
        FromDate: fromDate,
        ToDate: toDate,
        Days: totalDaysRange,
        Present: empRows.filter((r) => r.isPresent).length,
        Absent: empRows.filter((r) => r.isAbsent).length,
        Late: empRows.filter((r) => r.isLate).length,
        WorkingMinutes: empRows.reduce((a, b) => a + (b.workingMinutes || 0), 0),
        OvertimeMinutes: empRows.reduce((a, b) => a + (b.overtimeMinutes || 0), 0),
      };

      // SUMMARY TITLE
      rows.push(["EMPLOYEE SUMMARY"]);

      // SUMMARY HEADER
      rows.push(Object.keys(summaryRow));

      // SUMMARY VALUES
      rows.push(Object.values(summaryRow));

      // EMPTY ROW
      rows.push([]);

      // DETAIL HEADER
      rows.push([
        "EmpId",
        "Name",
        "Date",
        "First Punch",
        "Last Punch",
        "WorkingMinutes",
        "Overtime",
        "Status",
      ]);

      // DETAIL ROWS
      empRows.forEach((r) => {

        rows.push([
          r.empId,
          r.empName,
          r.attendanceDateDisplay,

          // Tab prefix prevents Excel from mangling time values
          r.firstPunchDisplay ? `\t${r.firstPunchDisplay}` : "",
          r.lastPunchDisplay ? `\t${r.lastPunchDisplay}` : "",

          r.workingMinutes || 0,
          r.overtimeMinutes || 0,

          r.isAbsent ? "Absent" : r.isLate ? "Late" : "Present",
        ]);
      });

      rows.push([]);
      rows.push([]);
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((row) => row.map(escapeCSV).join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = `Attendance_Report_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ================= UI ================= */

  return (

    <div className="bg-white p-5 rounded-2xl shadow border">

      {/* ================= HEADER ================= */}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">

        <h2 className="text-xl font-semibold text-gray-800">
          Periodic Attendance Report
        </h2>

        <div className="flex flex-wrap gap-2">

          {/* FROM DATE */}

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          />

          {/* TO DATE */}

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          />

          {/* EXPORT */}

          <button
            onClick={exportExcel}
            disabled={!groupedEmployees.length}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <FaFileExcel />
            Export
          </button>

        </div>
      </div>

      {/* ================= TABLE ================= */}

      <div className="border rounded-xl overflow-auto max-h-[600px]">

        <table className="w-full text-sm">

          <thead className="bg-gray-200 sticky top-0 z-10">
            <tr>
              <th className="p-2 border">ID</th>
              <th className="p-2 border text-left">Name</th>
              <th className="p-2 border">Date</th>
              <th className="p-2 border">First Punch</th>
              <th className="p-2 border">Last Punch</th>
              <th className="p-2 border">Working Min</th>
              <th className="p-2 border">OT Min</th>
              <th className="p-2 border">Status</th>
            </tr>
          </thead>

          <tbody>

            {loading ? (

              <tr>
                <td colSpan="8" className="text-center p-5 text-gray-500">
                  Loading...
                </td>
              </tr>

            ) : groupedEmployees.length === 0 ? (

              <tr>
                <td colSpan="8" className="text-center p-5 text-gray-400">
                  No data found for the selected range.
                </td>
              </tr>

            ) : (

              groupedEmployees.map((empRows, index) => {

                const emp = empRows[0];

                const present = empRows.filter((r) => r.isPresent).length;
                const absent = empRows.filter((r) => r.isAbsent).length;
                const late = empRows.filter((r) => r.isLate).length;

                const workingMinutes = empRows.reduce(
                  (a, b) => a + (b.workingMinutes || 0),
                  0
                );

                const overtimeMinutes = empRows.reduce(
                  (a, b) => a + (b.overtimeMinutes || 0),
                  0
                );

                return (

                  <React.Fragment key={index}>

                    {/* EMPLOYEE SUMMARY ROW */}

                    <tr className="bg-blue-100">
                      <td colSpan="8" className="p-3 font-semibold">
                        <div className="flex flex-wrap gap-5">

                          <span>👤 {emp.empName}</span>

                          <span>📅 Days: {empRows.length}</span>

                          <span className="text-green-700">
                            ✅ Present: {present}
                          </span>

                          <span className="text-red-700">
                            ❌ Absent: {absent}
                          </span>

                          <span className="text-yellow-700">
                            ⏰ Late: {late}
                          </span>

                          <span>🕒 Work: {workingMinutes} min</span>

                          <span>🚀 OT: {overtimeMinutes} min</span>

                        </div>
                      </td>
                    </tr>

                    {/* DAILY DETAIL ROWS */}

                    {empRows.map((row, i) => (

                      <tr key={i} className="hover:bg-blue-50">

                        <td className="p-2 border text-center">{row.empId}</td>

                        <td className="p-2 border">{row.empName}</td>

                        <td className="p-2 border text-center">
                          {row.attendanceDateDisplay}
                        </td>

                        <td className="p-2 border text-center">
                          {row.firstPunchDisplay || "—"}
                        </td>

                        <td className="p-2 border text-center">
                          {row.lastPunchDisplay || "—"}
                        </td>

                        <td className="p-2 border text-center">
                          {row.workingMinutes || 0}
                        </td>

                        <td className="p-2 border text-center">
                          {row.overtimeMinutes || 0}
                        </td>

                        <td className="p-2 border text-center">
                          {row.isAbsent ? (
                            <span className="text-red-600 font-medium">
                              🔴 Absent
                            </span>
                          ) : row.isLate ? (
                            <span className="text-yellow-600 font-medium">
                              🟡 Late
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium">
                              🟢 Present
                            </span>
                          )}
                        </td>

                      </tr>
                    ))}

                  </React.Fragment>
                );
              })
            )}

          </tbody>

        </table>

      </div>

    </div>
  );
};

export default PeriodicReportByRest;